import { useQuery } from '@tanstack/react-query';
import { loadVariantLinks, type LinkRow } from '@/shared/variantMapping/variantLinks';
import type { MatchableLink } from '@/shared/variantMapping/poCodeMatching';

function toMatchableLinks(rows: LinkRow[]): MatchableLink[] {
  const links: MatchableLink[] = [];
  for (const row of rows) {
    if (!row.master_product_id || !row.identifier_value) continue;
    links.push({
      masterProductId: row.master_product_id,
      identifierValue: row.identifier_value,
      identifierType: row.identifier_type,
      active: row.active,
    });
  }
  return links;
}

/**
 * Every identifier link in the organisation, inactive ones included.
 *
 * FIG-438 needs the inactive rows too: a link an admin removed must not be
 * reported as matched, and must not be made again, so the rules have to be able
 * to see that it was switched off rather than never existing.
 *
 * On a test clone the links it inherits from its source organisation are merged
 * in, the way funding resolves them, so the badge and the figures agree.
 */
export function useVariantIdentifierLinks(organisationId: string) {
  return useQuery<MatchableLink[]>({
    queryKey: ['variant-identifier-links', organisationId],
    enabled: !!organisationId,
    queryFn: async () => {
      return toMatchableLinks(await loadVariantLinks(organisationId));
    },
  });
}
